/**
 * Eric 2026-06-25 (iter125 — Session A12) — Bandeaux Pipeline Progress +
 * Failed status. Extrait de VideoDetailPage.js (-83 lignes).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, CircleNotch, Warning, PauseCircle, Play, CheckCircle, CaretDown, CaretUp, Prohibit } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { PIPELINE_STAGES, pipelineStageLabel } from "../../lib/videoDetailFormat";
import { ETAPES_VERIFIEES, etatEtape, raisonEtape, formaterDate } from "../../lib/etapesPipeline"; // Tâche 1493
import { confirmDialog } from "../../lib/uiDialogs";
import { DiagnosticEchecPanel } from "./DiagnosticEchecPanel";
import EtaAttente from "./EtaAttente"; // Tâche 1476
import MenuContinuerPipeline from "./MenuContinuerPipeline"; // Tâche 1493

export const VideoPipelineStatus = (props) => {
  const { lang, api, video, videoId, isProcessing, isFailed, pipelineStatus, startPolling, loadVideoData, pipelineFlags, videoUseDiarisation, videoUseFacialId, canEdit } = props;
  // Tâches 1355 · 1356 (Eric 2026-06-30) — Le bandeau du pipeline restait en
  // français dans la version anglaise (« Traitement en cours »,
  // « Forcer l'arrêt »…). Même motif que le reste de l'application.
  const tr = (fr, en) => (lang === "fr" ? fr : en);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);
  // B1105 (Eric 2026-09-08) — « Forcer l'arrêt du pipeline ». La pause laisse
  // le pipeline reprenable ; ici Eric veut COUPER un pipeline qui boucle sur
  // un bogue pour qu'il cesse de consommer des crédits. La file tourne avec
  // un worker en mémoire : l'étape en cours finit, mais aucune suivante ne
  // démarre (contrôle coopératif côté serveur, `core/ai/cancellation.py`).
  const doForceStop = async () => {
    if (stopBusy) return;
    const ok = await confirmDialog(
      tr(
        "Forcer l'arrêt du pipeline ?\n\n" +
          "• L'étape en cours se termine (impossible de l'interrompre en plein vol)\n" +
          "• Aucune étape suivante ne démarrera — plus aucun crédit consommé\n" +
          "• La vidéo passe en « arrêté » : « Recommencer (transcription) » repart de zéro\n\n" +
          "À utiliser quand le pipeline boucle sur un bogue.",
        "Force the pipeline to stop?\n\n" +
          "• The current step finishes (it cannot be interrupted mid-flight)\n" +
          "• No further step will start — no more credits consumed\n" +
          "• The video moves to \"stopped\": \"Restart (transcript)\" starts over\n\n" +
          "Use this when the pipeline loops on a bug.",
      )
    );
    if (!ok) return;
    setStopBusy(true);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/queue/cancel?conclure_pipeline=true`, {},
        { withCredentials: true, _allowRetry: true }
      );
      const saved = data?.saved_estimate;
      // B1117 (Eric 2026-09-08) — Chiffrer ce que l'arrêt a évité : les
      // étapes et appels sont solides, le montant est une estimation aux
      // tarifs publics (aucun coût réel par étape n'est enregistré).
      const savedLine = saved?.model_calls_skipped
        ? tr(
            ` ${saved.stages_skipped_count} étape(s) et ${saved.model_calls_skipped} appel(s) de modèle évités — environ ${saved.usd_estimate} $ US (estimation, tarifs publics).`,
            ` ${saved.stages_skipped_count} step(s) and ${saved.model_calls_skipped} model call(s) avoided — about US$${saved.usd_estimate} (estimate, public rates).`,
          )
        : "";
      // Tâche 1487 (Eric) — Dire ce que l'arrêt a CONCLU : terminé si des
      // connaissances existent, partiellement complété sinon (avec ce qui
      // manque). Sans cette phrase, l'utilisateur ne sait pas si son arrêt a
      // sauvé du travail ou coupé une vidéo vide.
      const conclusionLine = data?.pipeline_complet
        ? tr(" Pipeline affiché comme TERMINÉ : des connaissances ont déjà été générées.",
             " Pipeline shown as COMPLETE: knowledge has already been generated.")
        : tr(
            ` Pipeline affiché comme PARTIELLEMENT COMPLÉTÉ — il manque : ${(data?.manquants || []).join(", ") || "tout"}.`,
            ` Pipeline shown as PARTIALLY COMPLETE — missing: ${(data?.manquants || []).join(", ") || "everything"}.`,
          );
      toast.success((data?.note || tr("Arrêt du pipeline demandé", "Pipeline stop requested")) + savedLine + conclusionLine, { duration: 8000 });
      if (loadVideoData) await loadVideoData();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?._friendlyMessage || tr("Impossible d'arrêter le pipeline", "Could not stop the pipeline");
      toast.error(msg);
    } finally {
      setStopBusy(false);
    }
  };
  // Vincent B866 (Eric 2026-02-14 SPEC STRICK PROD) — Triangle
  // collapse/expand en haut à droite du rectangle noir Pipeline.
  // Ouvert par défaut pendant le travail (état initial `false`).
  // Une fois le traitement terminé, l'utilisateur peut cliquer pour
  // le refermer et libérer de l'espace vertical. Le rectangle reste
  // TOUJOURS déplié tant que le pipeline tourne (garde-fou dans le
  // handler onClick — voir plus bas).
  const [pipelineCollapsed, setPipelineCollapsed] = useState(false);
  // Tâche 1493 (Eric 2026-06) — Ce qui a RÉELLEMENT été fait, demandé au
  // serveur. Sans cela, les pastilles ne connaissaient que `pipeline_stage` :
  // une vidéo arrêtée en « pause — révision fusion » (60 %) affichait
  // « Fusion des transcriptions » en gris alors que la fusion existait en
  // base depuis des semaines, faite à la main depuis l'onglet Transcriptions.
  const [etapesReelles, setEtapesReelles] = useState(null);
  const chargerEtapes = useCallback(async () => {
    if (!canEdit || !videoId) return;
    try {
      const { data } = await axios.get(
        `${api}/api/videos/${videoId}/pipeline/etapes`, { withCredentials: true });
      setEtapesReelles(data);
    } catch (err) {
      // Le bandeau retombe sur l'ancien calcul par pourcentage : moins
      // précis, mais jamais vide.
      setEtapesReelles(null);
    }
  }, [api, videoId, canEdit]);
  useEffect(() => { chargerEtapes(); }, [chargerEtapes, video?.pipeline_status, video?.pipeline_stage]);
  // Vincent B561/B567 (Eric 2026-07-27) — 2 boutons pause/reprise à gauche du
  // bandeau pipeline (sur la page Vidéo, sous « Termes du dictionnaire… »).
  // Réutilise les endpoints existants `/api/videos/{id}/queue/pause` et
  // `/api/videos/{id}/queue/resume` déjà présents dans VideoQueuePage/FileExplorer.
  const doPause = async () => {
    if (pauseBusy) return;
    setPauseBusy(true);
    try {
      await axios.post(`${api}/api/videos/${videoId}/queue/pause`, {}, { withCredentials: true, _allowRetry: true });
      toast.success(tr("Pipeline mis en pause", "Pipeline paused"));
      if (loadVideoData) await loadVideoData();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?._friendlyMessage || tr("Impossible de mettre en pause", "Could not pause");
      toast.error(msg);
    } finally {
      setPauseBusy(false);
    }
  };
  const doResume = async () => {
    if (pauseBusy) return;
    setPauseBusy(true);
    try {
      await axios.post(`${api}/api/videos/${videoId}/queue/resume`, {}, { withCredentials: true, _allowRetry: true });
      toast.success(tr("Pipeline relancé", "Pipeline resumed"));
      if (loadVideoData) await loadVideoData();
      if (startPolling) startPolling();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?._friendlyMessage || tr("Impossible de relancer", "Could not resume");
      toast.error(msg);
    } finally {
      setPauseBusy(false);
    }
  };

  // Vincent B585 (Eric 2026-07-27 18h15) — 2 boutons à gauche du bandeau
  // pipeline en pause (paused_pending_fusion / paused_pending_extraction) :
  //   « Continuer (connaissances) »   → extraction directe (skip pause2)
  //   « Recommencer (transcription) » → refaire Whisper depuis zéro
  //
  // ⚠️ Tâche 1493 (Eric 2026-06) — `doContinueKnowledge` est SUPPRIMÉ : il
  // appelait `resume-after-fusion?skip_pause2=true`, qui refaisait la FUSION
  // puis l'extraction, sans demander. Refaire la fusion était donc le prix
  // obligatoire pour extraire, sur un texte le plus souvent déjà fusionné.
  // Le menu `MenuContinuerPipeline` le remplace : une option par étape, avec
  // « Faire » ou « Refaire » selon ce qui existe vraiment en base.
  const doRestartTranscription = async () => {
    if (resumeBusy) return;
    // B1090 — Confirmation destructive quand la vidéo est déjà traitée : on
    // efface une transcription valide pour la refaire.
    if (video?.pipeline_status === "complete") {
      const ok = await confirmDialog(
        tr(
          "Recommencer la transcription depuis le début ?\n\n" +
            "• La transcription Whisper actuelle sera supprimée\n" +
            "• Les segments et le mapping des locuteurs seront effacés\n" +
            "• Le pipeline sera relancé (plusieurs minutes)\n\n" +
            "La transcription fusionnée existante reste préservée.",
          "Restart the transcript from scratch?\n\n" +
            "• The current Whisper transcript will be deleted\n" +
            "• Segments and the speaker mapping will be erased\n" +
            "• The pipeline will be relaunched (several minutes)\n\n" +
            "The existing merged transcript is preserved.",
        )
      );
      if (!ok) return;
    }
    setResumeBusy(true);
    try {
      // Vincent B612 récurrence 3e passe (Eric 2026-07-31 SPEC STRICK PROD) —
      // ENVOIE l'état ACTUEL des cases Diarisation / ID faciale dans le body.
      // Sans ça, ce bouton (contrairement à « Refaire transcription Whisper »
      // dans WhisperToolbar et « Relancer TOUT le pipeline » dans SpeakersPanel)
      // relançait Whisper mais oubliait de persister les flags → la couleur
      // des mots « Diarisation (AssemblyAI) » / « Identification faciale »
      // dans la barre pipeline restait rouge/verte selon l'ANCIEN état.
      await axios.post(
        `${api}/api/videos/${videoId}/rerun-whisper`,
        {
          use_diarisation: videoUseDiarisation,
          use_facial_id: videoUseFacialId,
        },
        { withCredentials: true, _allowRetry: true }
      );
      toast.success(tr("Transcription Whisper relancée depuis le début", "Whisper transcript restarted from scratch"));
      if (loadVideoData) await loadVideoData();
      if (startPolling) startPolling();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?._friendlyMessage || tr("Impossible de recommencer", "Could not restart");
      toast.error(msg);
    } finally {
      setResumeBusy(false);
    }
  };

  // Vincent B567 (Eric 2026-07-27, précision 15h15) — Le bandeau pipeline
  // doit rester visible EN TOUT TEMPS, que le pipeline soit en cours, en
  // pause, terminé (complete) ou même échoué. Vincent veut pouvoir suivre
  // l'état à tout moment sans naviguer ailleurs.
  // Vincent B581 récurrence (Eric B695 2026-02-08) — Priorité DB pour
  // l'état « complete » : quand la DB confirme que le pipeline est
  // terminé, on ignore un éventuel status "processing" laissé par le
  // poll (race courte : le fetch DB revient avant que le poll ne
  // rafraîchisse). Sans ça, le bandeau affichait « Traitement en cours »
  // alors que le badge sous le titre disait « Terminé » — la
  // contradiction que Vincent a signalée dans B581.
  const _pollStatus = pipelineStatus?.status;
  const _dbStatus = video.pipeline_status;
  const status = _dbStatus === "complete" ? "complete" : (_pollStatus || _dbStatus);
  const isPausedInPipeline = status === "paused_pending_fusion"
    || status === "paused_pending_extraction"
    || status === "paused_pending_user";
  // Vincent B581 (Eric 2026-07-27 18h15 + B695 durcissement 2026-02-08) —
  // Ne plus mentir avec « Traitement en cours » quand le pipeline est
  // terminé. Vrai dès que EITHER poll OU DB confirme « complete ».
  const isComplete = status === "complete"
    || _pollStatus === "complete"
    || _dbStatus === "complete";
  // Vincent B582 (Eric 2026-07-27 18h15) — Bandeau des étapes pipeline
  // strictement réservé aux utilisateurs autorisés à concevoir/corriger des
  // formations (concepteurs/admins/fondateurs). Les autres ne voient rien.
  if (!canEdit) return null;
  // Le bandeau est masqué UNIQUEMENT si aucune donnée pipeline n'existe
  // (vidéo tout juste uploadée qui n'a pas encore démarré).
  // Tâche 1487 (Eric) — ⚠️ Exception : un pipeline CONCLU comme partiel par
  // l'utilisateur n'a parfois jamais eu de `pipeline_status` (cas mesuré sur
  // un document texte retiré de la file). Sans cette exception, le bandeau
  // « Partiellement complété » restait invisible là où il est le plus utile.
  // Tâche 1493 (Eric) — ⚠️ Le bandeau s'affiche aussi quand une ÉTAPE a été
  // faite à la main sans que le pipeline soit jamais passé par là : c'est le
  // cas exact signalé (« le rectangle noir n'affiche pas lorsque des étapes
  // ont été réalisées à la main »). Une fusion ou des connaissances qui
  // existent en base méritent un bandeau, même sans `pipeline_status`.
  const _etapeFaite = !!(etapesReelles && (etapesReelles.fusion?.faite
    || etapesReelles.extraction?.faite || etapesReelles.indexation?.faite));
  const showPipeline = (!!status && status !== "pending") || !!video?.pipeline_partiel || _etapeFaite;
  return (
    <>
      {/* Tâche 1313 (Eric 2026-06-30) — L'alerte « enregistrement saturé »
          reste visible dans la chronologie, même des semaines après
          l'import : c'est la seule explication d'une transcription
          absurde, et elle ne doit pas disparaître avec le bandeau de la
          page d'import. Les niveaux mesurés restent au survol. */}
      {video?.audio_saturated && (
        <div
          className="mt-2 flex items-start gap-2 text-[11px] rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-900"
          data-testid="pipeline-audio-saturated"
          title={video.audio_saturation_detail || ""}
        >
          <span aria-hidden="true">⚠</span>
          <span>
            <span className="font-semibold">{tr("Enregistrement saturé", "Clipped recording")}</span>
            {tr(
              " — le micro écrête. Transcription et découpage des locuteurs peu fiables ; rien n'est réparable après coup.",
              " — the microphone is clipping. Transcript and speaker split are unreliable; nothing can be repaired afterwards.",
            )}
            {video.audio_saturation_detail ? (
              <span className="opacity-70 cursor-help"> {tr("(niveaux au survol)", "(levels on hover)")}</span>
            ) : null}
          </span>
        </div>
      )}
      {/* ── Pipeline Progress (during processing OR pause) ── */}
      {showPipeline && (() => {
        // Vincent B899 (Eric 2026-02-14 SPEC STRICK PROD) — L'utilisateur
        // peut désormais réduire le pipeline MÊME quand le traitement
        // n'est pas fini. Dans ce cas, on colore le bandeau réduit en
        // ROUGE (bg-red-950 + border-red-700 + icône rouge) pour bien
        // signaler « pas fini » et éviter que Vincent oublie qu'un
        // traitement est encore en cours après l'avoir replié. Palette
        // rouge foncé cohérente avec le fond zinc-900 d'origine —
        // reste lisible en dark, WCAG AA sur texte blanc.
        // NB : pas de persistance sessionStorage (choix explicite user)
        //       → à chaque remount, on ré-ouvre par défaut.
        const isCollapsedIncomplete = pipelineCollapsed && !isComplete && !isPausedInPipeline;
        const containerClass = isCollapsedIncomplete
          ? "bg-red-950 border border-red-700 rounded-lg p-3 mb-4 relative"
          : "bg-zinc-900 border border-zinc-800 rounded-lg p-3 mb-4 relative";
        const collapseBtnClass = isCollapsedIncomplete
          ? "absolute top-2 right-2 p-1 rounded transition-colors text-red-300 hover:text-white hover:bg-red-800 cursor-pointer"
          : "absolute top-2 right-2 p-1 rounded transition-colors text-zinc-400 hover:text-white hover:bg-zinc-800 cursor-pointer";
        return (
        <div
          className={containerClass}
          data-testid="pipeline-progress"
          data-collapsed={pipelineCollapsed ? "true" : "false"}
          data-collapsed-incomplete={isCollapsedIncomplete ? "true" : "false"}
        >
          {/* Vincent B866 → B899 (Eric 2026-02-14 SPEC STRICK PROD) —
              Triangle collapse en haut à droite. Depuis B899, cliquable
              en TOUT temps (y compris pendant que le pipeline tourne)
              — le bandeau replié devient rouge pour signaler l'état
              inachevé. */}
          <button
            type="button"
            onClick={() => setPipelineCollapsed((v) => !v)}
            className={collapseBtnClass}
            data-testid="pipeline-collapse-toggle"
            title={pipelineCollapsed
              ? (isCollapsedIncomplete
                  ? tr("Développer le pipeline (traitement encore en cours)", "Expand the pipeline (still processing)")
                  : tr("Développer le pipeline", "Expand the pipeline"))
              : tr("Réduire le pipeline", "Collapse the pipeline")}
            aria-label={pipelineCollapsed ? tr("Développer", "Expand") : tr("Réduire", "Collapse")}
          >
            {pipelineCollapsed ? <CaretDown size={14} weight="bold" /> : <CaretUp size={14} weight="bold" />}
          </button>
          {/* B1104 (Eric 2026-09-08) — `pr-9` réserve la gouttière du triangle
              de repli (positionné en absolu en haut à droite) et `flex-wrap`
              laisse la rangée passer à la ligne sur mobile : à 390 px, quatre
              boutons sur une seule ligne débordaient de l'écran. */}
          <div className="flex flex-wrap items-center gap-2 mb-1 pr-9" data-testid="pipeline-banner-row">
            {/* Vincent B949 (Eric 2026-02-14 SPEC STRICK PROD) — Single-line
                layout : icon + description courte à GAUCHE, puis les
                boutons contextuels (Recommencer / Continuer / Pause / Reprendre)
                à DROITE, tout sur une seule ligne pour réduire la hauteur
                du bandeau noir du pipeline. Avant B949 : icon+title
                empilaient sur 2 lignes + boutons empilaient sur 2 lignes,
                soit ~80px de haut au minimum ; maintenant ~30px. */}
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
              isComplete
                ? "bg-teal-500/20"
                : isCollapsedIncomplete
                  ? "bg-red-500/30"
                  : "bg-yellow-500/20"
            }`}>
              {isComplete
                ? <CheckCircle size={14} className="text-teal-400" weight="fill" />
                : isPausedInPipeline
                  ? <Warning size={12} className="text-amber-400" weight="fill" />
                  : <CircleNotch size={12} className={`animate-spin ${isCollapsedIncomplete ? "text-red-300" : "text-yellow-400"}`} />}
            </div>
            <div className="min-w-0 flex-shrink truncate">
              <span className="font-medium text-xs text-white mr-1.5">
                {/* Tâche 1487 (Eric) — Un pipeline arrêté par l'utilisateur
                    SANS connaissance autre que la synthèse n'est pas
                    « terminé » : il est partiellement complété, et le dire
                    évite de croire que les Q&R existent. */}
                {video?.pipeline_partiel
                  ? tr("Partiellement complété", "Partially complete")
                  : isComplete
                  ? tr("Traitement terminé", "Processing complete")
                  : isPausedInPipeline
                    ? tr("Pipeline en pause", "Pipeline paused")
                    : tr("Traitement en cours", "Processing")}
              </span>
              <span
                className={`text-[10px] ${video?.pipeline_partiel ? "text-amber-300" : "text-zinc-500"}`}
                title={video?.pipeline_partiel
                  ? tr(`Il manque : ${(video?.pipeline_manquants || []).join(", ") || "tout"}`,
                       `Missing: ${(video?.pipeline_manquants || []).join(", ") || "everything"}`)
                  : undefined}
                data-testid={video?.pipeline_partiel ? "pipeline-partiel-detail" : undefined}
              >
                {video?.pipeline_partiel
                  ? tr(`— arrêté par ${video?.pipeline_conclu_par || "un utilisateur"} · il manque : ${(video?.pipeline_manquants || []).join(", ") || "tout"}`,
                       `— stopped by ${video?.pipeline_conclu_par || "a user"} · missing: ${(video?.pipeline_manquants || []).join(", ") || "everything"}`)
                  : isComplete
                  ? tr("Toutes les étapes ont été complétées.", "Every step has been completed.")
                  : isPausedInPipeline
                    ? tr("— Action requise pour continuer", "— Action required to continue")
                    : tr("— Le pipeline d'IA analyse votre vidéo…", "— The AI pipeline is analysing your video…")}
              </span>
            </div>
            {/* Tâche 1476 (Eric) — Le temps restant estimé, sur la fiche de
                la vidéo elle-même : c'est là qu'on regarde une vidéo se
                traiter. Même composant que la File d'attente et
                l'Explorateur. */}
            <EtaAttente video={video} compact />
            {/* Boutons contextuels (à droite, tous sur la même ligne). */}
            {status === "processing" && (
              <button
                type="button"
                onClick={doPause}
                disabled={pauseBusy}
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-[10px] text-zinc-200 disabled:opacity-50 flex-shrink-0 ml-auto"
                data-testid="pipeline-banner-pause-btn"
                title={tr("Mettre le pipeline en pause", "Pause the pipeline")}
              >
                <PauseCircle size={12} weight="fill" /> Pause
              </button>
            )}
            {/* B1090 (Eric 2026-09-05) — Ces 2 boutons restent visibles EN
                TOUT TEMPS, même quand la vidéo est entièrement traitée :
                Vincent doit pouvoir refaire la transcription ou relancer les
                connaissances sans attendre une pause du pipeline. Ils sont
                désactivés pendant un traitement en cours (deux pipelines
                simultanés sur la même vidéo se marcheraient dessus). */}
            <>
                {/* Vincent B611 puis B949 — ordre visuel : Recommencer avant Continuer. */}
                <button
                  type="button"
                  onClick={doRestartTranscription}
                  disabled={resumeBusy || status === "processing"}
                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-[10px] text-zinc-200 disabled:opacity-50 flex-shrink-0 ml-auto"
                  data-testid="pipeline-banner-restart-transcription-btn"
                  title={status === "processing"
                    ? tr("Traitement en cours — attendez la fin avant de recommencer", "Processing — wait until it finishes before restarting")
                    : tr("Recommencer le pipeline depuis la transcription Whisper", "Restart the pipeline from the Whisper transcript")}
                >
                  <ArrowClockwise size={12} weight="bold" /> {tr("Recommencer (transcription)", "Restart (transcript)")}
                </button>
                <MenuContinuerPipeline
                  api={api}
                  video={video}
                  etapes={etapesReelles}
                  sizeClass="px-2 py-1 text-[10px]"
                  triggerClass={`bg-teal-700 hover:bg-teal-600 border border-teal-600 text-white ${status === "processing" ? "opacity-50 pointer-events-none" : ""}`}
                  onFait={async () => {
                    if (loadVideoData) await loadVideoData();
                    if (startPolling) startPolling();
                    await chargerEtapes();
                  }}
                />
                {/* B1105 (Eric 2026-09-08) — « Forcer l'arrêt » : dernier
                    bouton de la rangée, juste avant le triangle de repli (la
                    gouttière `pr-9` du conteneur garantit qu'aucun bouton ne
                    passe SOUS le triangle — B1104). Masqué quand la vidéo est
                    déjà terminée ou arrêtée : il n'y a plus rien à couper. */}
                {!isComplete && status !== "cancelled" && (
                  <button
                    type="button"
                    onClick={doForceStop}
                    disabled={stopBusy}
                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-900 hover:bg-red-800 border border-red-700 text-[10px] text-red-100 disabled:opacity-50 flex-shrink-0"
                    data-testid="pipeline-banner-force-stop-btn"
                    title={tr(
                      "Forcer l'arrêt du pipeline : l'étape en cours se termine, aucune suivante ne démarre (plus aucun crédit consommé)",
                      "Force-stop the pipeline: the current step finishes, no further step starts (no more credits consumed)",
                    )}
                  >
                    <Prohibit size={12} weight="bold" /> {stopBusy ? tr("Arrêt…", "Stopping…") : tr("Forcer l'arrêt", "Force stop")}
                  </button>
                )}
            </>
            {isPausedInPipeline && status === "paused_pending_user" && (
              <button
                type="button"
                onClick={doResume}
                disabled={pauseBusy}
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-[10px] text-zinc-200 disabled:opacity-50 flex-shrink-0 ml-auto"
                data-testid="pipeline-banner-resume-btn"
                title={tr("Reprendre le pipeline", "Resume the pipeline")}
              >
                <Play size={12} weight="fill" /> {tr("Reprendre", "Resume")}
              </button>
            )}
          </div>
          {/* Vincent B866 (Eric 2026-02-14 SPEC STRICK PROD) — Le
              contenu (barre + étapes) est caché quand pipelineCollapsed
              === true. En travail, `isComplete=false` et le bouton
              triangle est disabled → contenu toujours visible. */}
          {!pipelineCollapsed && (() => {
            const stage = pipelineStatus?.stage || video.pipeline_stage || "uploaded";
            const stageInfo = PIPELINE_STAGES[stage] || PIPELINE_STAGES.uploaded;
            // Backend `progress` is authoritative (already normalized + clamped).
            // Fall back to stage-derived pct if absent.
            const displayPct = (isComplete && !video?.pipeline_partiel)
              // Tâche 1491 (Eric) — Un pipeline TERMINÉ affiche 100 %. Le
              // statut en direct renvoie parfois une progression figée
              // (60 %, stade `awaiting_fusion`) après la fin : l'entête
              // disait « Toutes les étapes ont été complétées » pendant que
              // « Fusion des transcriptions », « Extraction des
              // connaissances » et « Indexation DB » restaient grises.
              // Deux affirmations contradictoires sur la même ligne.
              ? 100
              : (typeof pipelineStatus?.progress === "number" && pipelineStatus.progress >= 0)
              ? pipelineStatus.progress
              : stageInfo.pct;
            // Vincent B634 (Eric 2026-07-31 SPEC STRICK PROD) — La barre
            // horizontale colorée ne doit pas dépasser le mot de l'étape
            // courante. On calcule sa largeur d'après l'INDEX de l'étape
            // active dans la liste des étapes visibles (dots), pas d'après
            // `displayPct` qui donnait une barre 55% quand la dot
            // « Diarisation » était visuellement au ~43% de la ligne.
            // La barre se rafraîchit automatiquement à chaque changement
            // de stage (props qui alimentent ce composant).
            const _visibleStages = Object.entries(PIPELINE_STAGES)
              .filter(([k, v]) => k !== "complete" && k !== "uploaded" && !v.pseudo)
              .map(([k]) => k);
            let _idx = _visibleStages.indexOf(stage);
            const _isComplete = stage === "complete";
            // Vincent B638 (Eric 2026-07-31 SPEC STRICK PROD) — Quand le
            // pipeline est mis en pause après extraction audio +
            // transcription (paused_pending_fusion / paused_pending_extraction),
            // le stage devient un pseudo (`awaiting_fusion`,
            // `awaiting_extraction`, `transcription_complete`,
            // `fusion_running`) qui n'est PAS dans la liste des étapes
            // visibles → `_idx = -1` → barre à 0 % → elle disparaissait
            // à l'écran alors qu'on est en pleine progression.
            // Fix : on retombe sur la DERNIÈRE étape visible dont le pct
            // ≤ pct du pseudo-état courant, pour conserver la barre
            // colorée jusqu'à l'étape RÉELLEMENT atteinte.
            if (_idx < 0 && !_isComplete && typeof stageInfo?.pct === "number") {
              const currentPct = stageInfo.pct;
              _idx = _visibleStages.reduce((acc, k, i) => (
                PIPELINE_STAGES[k].pct <= currentPct ? i : acc
              ), -1);
            }
            const barPct = _isComplete
              ? 100
              : _idx >= 0
                ? Math.round(((_idx + 1) / _visibleStages.length) * 100)
                : 0;
            // Vincent (2026-02) ticket #520 — Spec STRICTE : les labels
            // affichés SOUS les dots doivent être EXACTEMENT ceux de
            // PIPELINE_STAGES (« Extraction de l'audio »,
            // « Transcription (Whisper) », « Diarisation (AssemblyAI) »,
            // « Fusion des transcriptions »,
            // « Extraction des connaissances (GPT) », « Indexation DB »).
            // Aucun raccourci ni renommage — les labels peuvent wrap sur
            // 2 lignes (CSS `whitespace-normal leading-tight`).
            return (
              <>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-1.5">
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${barPct}%`, background: "linear-gradient(90deg, #eab308, #facc15)" }}
                    data-testid="pipeline-bar-fill"
                    data-bar-pct={barPct}
                    data-current-stage={stage}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-zinc-500">
                  <span className="truncate mr-2">{pipelineStageLabel(stage, lang)}</span>
                  <span className="tabular-nums flex-shrink-0">{displayPct}%</span>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  {Object.entries(PIPELINE_STAGES).filter(([k, v]) => k !== "complete" && k !== "uploaded" && !v.pseudo).map(([key, val]) => {
                    // Vincent B559 (Eric 2026-07-25) — Cohérence affichage
                    // après un rerun-whisper : quand la vidéo a `whisper_rerun_at`
                    // récent (< 5 min) ET que le pipeline est repassé en stage
                    // « transcription » ou avant, les étapes POSTÉRIEURES à
                    // « transcription » NE PEUVENT PAS être marquées comme
                    // « terminées » (dot vert) car elles vont être ré-exécutées.
                    // Sans ce garde-fou, on affichait tous les dots verts au
                    // début du rerun (progress cached du run précédent) ce qui
                    // était incohérent avec le stage réel.
                    const _postTranscription = ["diarization", "facial_id", "fusion", "cross_validation", "extraction", "indexation"];
                    const _rerunRecent = video?.whisper_rerun_at
                      && (Date.now() - new Date(video.whisper_rerun_at).getTime()) < 5 * 60 * 1000;
                    const _isPostTranscription = _postTranscription.includes(key);
                    const _pipelineBeforeOrAtTranscription = ["download", "audio_extraction", "transcription"].includes(stage);
                    const _mustRerun = _rerunRecent && _isPostTranscription && _pipelineBeforeOrAtTranscription;
                    const isActive = stage === key;
                    // Vincent B649 (Eric 2026-08-05 SPEC STRICK PROD) — Comparaison
                    // `>=` (au lieu de `>` strict) : quand le pipeline pause au
                    // stade `awaiting_fusion` (pseudo pct=60), la puce
                    // `facial_id` (val.pct=60) restait grise au lieu de passer
                    // en teal — l'utilisateur voyait « Identification faciale »
                    // en gris comme les étapes à venir, alors que la
                    // reconnaissance visuelle venait tout juste d'être terminée
                    // par le backend (elle s'exécute pendant le stage
                    // `diarization` et se conclut à l'entrée de
                    // `awaiting_fusion`). `!isActive` exclut l'étape actuellement
                    // en cours pour préserver la pulse jaune (sinon la puce
                    // active passerait en teal au lieu de rester yellow).
                    const isDone = !_mustRerun && !isActive && displayPct >= val.pct;
                    // Tâche 1493 (Eric 2026-06) — Pour les trois étapes que
                    // le serveur sait VÉRIFIER (fusion, extraction des
                    // connaissances, indexation de la base), le fait en base
                    // l'emporte sur le pourcentage : c'est tout l'objet de la
                    // tâche. Trois états au lieu de deux —
                    //   vert  = faite ET à jour
                    //   ambre = faite MAIS la source a changé depuis, ou sa
                    //           fraîcheur est inconnue (on le DIT au survol)
                    //   gris  = jamais faite
                    // Une étape faite sur un texte remplacé affichée en vert
                    // ferait croire que le travail est à jour.
                    const _cleVerifiee = ETAPES_VERIFIEES[key];
                    const _detailEtape = _cleVerifiee && etapesReelles
                      ? etapesReelles[_cleVerifiee]
                      : null;
                    const _etatReel = _detailEtape ? etatEtape(_detailEtape) : null;
                    const _faitEnBase = _etatReel === "a_jour";
                    const _perimee = _etatReel === "perimee";
                    // Bug 536 (Eric 2026-02-14) — Coloration ROUGE
                    // conditionnelle des libellés diarization et facial_id
                    // quand la case globale correspondante est OFF.
                    // Vincent B550 (Eric 2026-07-24) — Étendu au niveau
                    // per-video : si l'utilisateur a bypassé la diarisation
                    // ou l'identification faciale POUR CETTE vidéo lors du
                    // dernier reprocess, on colore aussi en rouge. Sinon
                    // vert (ON) via la logique existante (isDone/isActive).
                    // Priorité : rouge > actif (bleu) > terminé (vert) > à venir.
                    // Vincent B12 récurrence (Eric 2026-08-01 SPEC STRICK PROD)
                    // — Aligne la logique frontend avec le backend
                    // (`core/ai/pipeline.py` ligne 240) : l'opt-in explicite
                    // per-video PRIME sur le kill switch global. Sans ça,
                    // Vincent voyait le mot « Identification faciale » rester
                    // rouge après une 2ᵉ transcription avec `use_facial_id=true`
                    // parce que le kill switch org `skip_visual_speaker_id=true`
                    // forçait la couleur rouge, même quand la vidéo avait
                    // explicitement activé l'ID faciale.
                    const _diarOptInExplicit = videoUseDiarisation === true;
                    const _facialOptInExplicit = videoUseFacialId === true;
                    const isDisabledFlag =
                      (key === "diarization" && (
                        (pipelineFlags?.skip_assemblyai_diarization === true && !_diarOptInExplicit)
                        || videoUseDiarisation === false
                      )) ||
                      (key === "facial_id" && (
                        (pipelineFlags?.skip_visual_speaker_id === true && !_facialOptInExplicit)
                        || videoUseFacialId === false
                      ));
                    const labelColorClass = isDisabledFlag
                      ? "text-red-500 font-semibold"
                      : (isActive
                        ? "text-yellow-400 font-semibold"
                        : _perimee
                          ? "text-amber-400"
                          : (_faitEnBase || (isDone && _etatReel === null))
                            ? "text-teal-500/80"
                            : _etatReel === "jamais"
                              ? "text-zinc-600"
                              : isDone
                                ? "text-teal-500/80"
                                : "text-zinc-600");
                    // Vincent B612+audit (Eric 2026-07-29 22h) — Tooltip
                    // enrichi avec l'historique du dernier changement de
                    // toggle (qui l'a changé, quand, ON/OFF). Law 25 :
                    // traçabilité audit — évite le mystère « qui a coupé
                    // la diarisation ? ». `toggle_audit_log` est un tableau
                    // embedded sur video ; on ne montre que la dernière
                    // entrée du champ correspondant.
                    const _fieldForKey = key === "diarization"
                      ? "use_diarisation"
                      : key === "facial_id"
                        ? "use_facial_id"
                        : null;
                    let _auditLine = "";
                    if (_fieldForKey && Array.isArray(video?.toggle_audit_log)) {
                      const entries = video.toggle_audit_log
                        .filter(e => e?.field === _fieldForKey && e?.changed_at)
                        .sort((a, b) => (b.changed_at || "").localeCompare(a.changed_at || ""));
                      const last = entries[0];
                      if (last) {
                        const verb = last.new_value ? "Activé" : "Désactivé";
                        const by = last.user_name || last.user_email || "Utilisateur";
                        let when = last.changed_at;
                        try {
                          const d = new Date(last.changed_at);
                          if (!Number.isNaN(d.getTime())) {
                            const opts = { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" };
                            when = d.toLocaleString(lang === "en" ? "en-CA" : "fr-CA", opts);
                          }
                        } catch (_) { /* fallback à ISO brut */ }
                        _auditLine = lang === "en"
                          ? `\n${verb === "Activé" ? "Enabled" : "Disabled"} by ${by} on ${when}`
                          : `\n${verb} par ${by} le ${when}`;
                      }
                    }
                    const _baseTitle = isDisabledFlag
                      ? (key === "diarization"
                          ? (lang === "en" ? "Diarization disabled" : "Diarisation désactivée")
                          : (lang === "en" ? "Facial identification disabled" : "Identification faciale désactivée"))
                      : (key === "diarization"
                          ? (lang === "en" ? "Diarization enabled" : "Diarisation activée")
                          : key === "facial_id"
                            ? (lang === "en" ? "Facial identification enabled" : "Identification faciale activée")
                            : pipelineStageLabel(key, lang));
                    const _titleAttr = _fieldForKey
                      ? (_baseTitle + _auditLine)
                      : (isDisabledFlag ? _baseTitle : undefined);
                    // Tâche 1493 — Le survol d'une étape vérifiée dit ce qui
                    // s'est passé : quand, par qui, et pourquoi elle est
                    // ambre. Sans cette phrase, « ambre » resterait une
                    // couleur sans explication.
                    let _titreEtape = _titleAttr;
                    if (_detailEtape) {
                      const _quand = formaterDate(_detailEtape.le, lang);
                      _titreEtape = _etatReel === "jamais"
                        ? tr("Jamais faite.", "Never done.")
                        : `${_quand
                            ? tr(`Faite le ${_quand}`, `Done on ${_quand}`)
                            : tr("Faite (date inconnue)", "Done (date unknown)")}${
                            _detailEtape.par ? ` · ${_detailEtape.par}` : ""}${
                            _perimee ? `\n${raisonEtape(_detailEtape, lang)}` : ""}`;
                    }
                    // Les pastilles des étapes vérifiées suivent l'état réel ;
                    // les autres gardent le calcul par pourcentage.
                    const _classePastille = _etatReel === "perimee"
                      ? "bg-amber-400"
                      : _etatReel === "a_jour"
                        ? "bg-teal-500"
                        : _etatReel === "jamais"
                          ? "bg-zinc-700"
                          : isDone
                            ? "bg-teal-500"
                            : isActive
                              ? "bg-yellow-400 animate-pulse shadow-lg shadow-yellow-500/50"
                              : "bg-zinc-700";
                    return (
                      <div key={key} className="flex-1 flex flex-col items-center min-w-0 px-0.5">
                        {/* Vincent B569 (Eric 2026-07-27) — Étape en cours en JAUNE
                            (auparavant bleu #002FA7). Contraste maximal sur fond
                            zinc-900. Safe daltonien : jaune ≠ teal (done) ≠ rouge
                            (disabled). */}
                        <div
                          className={`w-2 h-2 rounded-full transition-all ${isActive && _etatReel === null ? "bg-yellow-400 animate-pulse shadow-lg shadow-yellow-500/50" : _classePastille}`}
                          data-testid={_cleVerifiee ? `pipeline-dot-${key}` : undefined}
                          data-etat-reel={_etatReel || undefined}
                        />
                        <span
                          className={`text-[9px] mt-0.5 leading-tight text-center whitespace-normal break-words ${labelColorClass} ${(_fieldForKey || _detailEtape) ? "cursor-help" : ""}`}
                          title={_titreEtape}
                          data-testid={_fieldForKey ? `pipeline-toggle-label-${key}` : undefined}
                        >
                          {pipelineStageLabel(key, lang)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {pipelineStatus?.logs?.[0] && (
                  <div className="mt-2 text-[10px] text-zinc-500 bg-zinc-800/50 px-2 py-1 rounded font-mono truncate" title={pipelineStatus.logs[0].message}>
                    {pipelineStatus.logs[0].message}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        );
      })()}

      {/* ── Failed status ──
          Vincent B872 (Eric 2026-02-14 SPEC STRICK PROD) — Refonte
          palette lisibilité daltonien-safe. Avant : palette dark mode
          (red-300 sur bg-red-950/30) rendait texte quasi-invisible en
          thème light. Après : fond red-50 pâle + texte red-900 foncé
          (ratio contraste > 7:1 = AAA WCAG) + border red-400. Le
          contraste est basé sur la LUMINOSITÉ (foncé/clair), donc
          reste lisible pour deutéranopie / protanopie. Message
          d'erreur passé en `<pre>` monospace + wrap pour les
          commandes ffmpeg qui débordent. */}
      {isFailed && (
        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 mb-4 flex items-start justify-between gap-3" data-testid="pipeline-failed">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Warning size={22} className="text-red-700 shrink-0 mt-0.5" weight="fill" />
            {/* Tâche 1400 (Eric 2026-09-30) — La trace brute (quarante lignes
                de configuration ffmpeg, un code HTTP, un nom de classe
                Python) ne disait ni la cause ni quoi faire. Elle reste
                accessible sous « Détails techniques » : c'est la preuve, et
                c'est elle qu'on transmet quand la cause n'est pas reconnue. */}
            <DiagnosticEchecPanel video={video} />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await axios.post(`${api}/api/videos/${videoId}/reprocess`, {}, { withCredentials: true });
              startPolling();
              loadVideoData();
            }}
            className="border-red-600 bg-white text-red-800 hover:bg-red-100 hover:text-red-900 font-semibold shrink-0"
            data-testid="reprocess-btn"
          >
            <ArrowClockwise size={14} className="mr-1" /> {tr("Relancer", "Relaunch")}
          </Button>
        </div>
      )}

    </>
  );
};

export default VideoPipelineStatus;
